/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getUrlSync, IActionContext } from '@msdyn365-commerce/core';
import { ICheckoutState } from '@msdyn365-commerce/global-state';
import { SalesOrder, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { CartCheckout } from './checkout';
import { EmptyActiveCart } from './empty-active-cart';
import { EmptyActiveCartInvoice } from './empty-active-cart-invoice';

/**
 * Known action error failure states
 */
const enum ActionErrorStatus {
    Timeout = 'Timeout',
    HookTimeout = 'Hook Timeout',
    ServiceUnavailable = 'Service Unavailable'
}

export const PlaceOrder = async (
    ctx: IActionContext,
    checkoutState: ICheckoutState | undefined,
    orderedProducts: SimpleProduct[] | undefined,
    redirect: boolean,
    updatedCartVersion?: number,
    isPaymentVerificationRedirection?: boolean
) => {
    return new Promise<void>(async (resolve: () => void, reject: (reason?: Error) => void) => {
        ctx.telemetry.information('Checkout place-order method is triggered.');

        let salesOrder: SalesOrder;

        // Proceed checkout
        try {
            salesOrder = await CartCheckout(ctx, updatedCartVersion, isPaymentVerificationRedirection);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error) {
            if (error.status === ActionErrorStatus.Timeout || error.status === ActionErrorStatus.ServiceUnavailable) {
                console.error('Error: Checkout failed due to timeout or service unavailable.');
                ctx.telemetry.information('Checkout emptyActiveCart is called.');
                await EmptyActiveCartInvoice(ctx);
            }
            reject(error);
            return;
        }

        // Remove purchased items from the active cart
        try {
            ctx.telemetry.information('Checkout emptyActiveCart is called.');
            await EmptyActiveCart(ctx);
        } catch (error) {
            reject(error);
            return;
        }

        if (redirect) {
            ctx.telemetry.information('Redirect to order confirmation page.');

            // Redirect to the order confirmation page
            const orderConfirmationUrl = getUrlSync('orderConfirmation', ctx) || '';
            if (!orderConfirmationUrl) {
                reject(new Error('Error: No orderConfirmationUrl'));
                return;
            }

            const separator = orderConfirmationUrl.includes('?') ? '&' : '?';
            const url = `${orderConfirmationUrl}${separator}transactionId=${salesOrder.Id}`;
            window.location.assign(url);
        } else {
            ctx.telemetry.information('Not redirect to order confirmation page.');

            if (checkoutState) {
                const result = await checkoutState.updateSalesOrder({
                    newSalesOrder: salesOrder,
                    newOrderedProducts: orderedProducts || []
                });

                if (result.status === 'FAILED') {
                    reject(new Error('Error: Updating error state failed'));
                    return;
                }
            } else {
                reject(new Error('Error: No checkout state'));
                return;
            }
            resolve();
        }
    });
};
